import axios, { AxiosInstance } from 'axios'
import { getCsrfToken } from './utils'
import type {
  PipelineRun,
  PipelineRunsResponse,
  PipelineRunSummary,
  CreatePipelineRunPayload,
  PipelineConfigSummary,
  PipelineConfigsResponse,
  PipelineConfig,
  OutputFile,
  LogsResponse,
  TenantDetail,
  TenantSession,
  TestFileSummary,
  TestResultSummary,
  PendingReview,
  HealthResponse,
} from '@/types/triplets'

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

function getBackendURL(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL.replace(/\/api$/, '')
  }

  const hostname = window.location.hostname

  if (hostname === 'localhost' || hostname.includes('.localhost')) {
    return ''
  }
  if (hostname === 'app.devfctrz.com' || hostname.endsWith('.devfctrz.com')) {
    return `${window.location.protocol}//api.devfctrz.com`
  }
  if (hostname === 'app.chatterpillar.io' || hostname.endsWith('.chatterpillar.io')) {
    return `${window.location.protocol}//api.chatterpillar.io`
  }

  return ''
}

const BACKEND_URL = getBackendURL()

const tripletsApi: AxiosInstance = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true,
})

function getTenantSubdomain(): string | null {
  try {
    const stored = localStorage.getItem('tenant')
    if (stored) {
      const tenant = JSON.parse(stored)
      if (tenant.subdomain) return tenant.subdomain
    }
  } catch {
    // ignore
  }

  const hostname = window.location.hostname
  if (
    hostname === 'app.devfctrz.com' ||
    hostname === 'app.chatterpillar.io' ||
    hostname === 'app.localhost'
  ) {
    return null
  }

  for (const domain of ['.devfctrz.com', '.chatterpillar.io', '.localhost']) {
    if (hostname.includes(domain)) {
      const parts = hostname.split(domain)[0]
      return parts.includes('.') ? null : parts
    }
  }

  return null
}

tripletsApi.interceptors.request.use((config) => {
  const csrfToken = getCsrfToken()
  if (csrfToken) config.headers['X-CSRFToken'] = csrfToken

  const sub = getTenantSubdomain()
  if (sub) config.headers['X-Tenant-Subdomain'] = sub

  return config
})

tripletsApi.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) window.location.href = '/login'
    return Promise.reject(error)
  },
)

// ---------------------------------------------------------------------------
// Pipeline Runs
// ---------------------------------------------------------------------------

export async function getRuns(params?: Record<string, string>): Promise<PipelineRunsResponse> {
  const response = await tripletsApi.get('/triplets/pipelines/', { params })
  return response.data
}

export async function createRun(payload: CreatePipelineRunPayload): Promise<PipelineRunSummary> {
  const response = await tripletsApi.post('/triplets/pipelines/', payload)
  return response.data
}

export async function getRun(runId: number | string): Promise<PipelineRun> {
  const response = await tripletsApi.get(`/triplets/pipelines/${runId}/`)
  return response.data
}

export async function cancelRun(runId: number | string): Promise<void> {
  await tripletsApi.delete(`/triplets/pipelines/${runId}/`)
}

export async function syncRun(runId: number | string): Promise<PipelineRun> {
  const response = await tripletsApi.post(`/triplets/pipelines/${runId}/sync/`)
  return response.data
}

export async function getLogs(runId: number | string, n = 200): Promise<LogsResponse> {
  const response = await tripletsApi.get(`/triplets/pipelines/${runId}/logs/`, { params: { n } })
  return response.data
}

export async function getOutputs(runId: number | string): Promise<OutputFile[]> {
  const response = await tripletsApi.get(`/triplets/pipelines/${runId}/outputs/`)
  return response.data
}

export async function getOutput(runId: number | string, filename: string): Promise<unknown> {
  const response = await tripletsApi.get(`/triplets/pipelines/${runId}/outputs/${filename}/`)
  return response.data
}

export async function getOutputDownloadUrl(
  runId: number | string,
  filename: string,
): Promise<{ url: string; filename: string }> {
  const response = await tripletsApi.get(
    `/triplets/pipelines/${runId}/outputs/${filename}/download/`,
  )
  return response.data
}

export async function uploadFiles(
  runId: number | string,
  files: File[],
): Promise<{ uploaded: string[] }> {
  const formData = new FormData()
  files.forEach((f) => formData.append('files', f))
  const response = await tripletsApi.post(`/triplets/pipelines/${runId}/upload/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export async function getUploadUrls(
  runId: number | string,
  filenames: string[],
): Promise<{ urls: { filename: string; url: string; method: string }[] }> {
  const response = await tripletsApi.post(`/triplets/pipelines/${runId}/upload-urls/`, {
    filenames,
  })
  return response.data
}

export async function uploadComplete(
  runId: number | string,
): Promise<{ status: string }> {
  const response = await tripletsApi.post(`/triplets/pipelines/${runId}/upload-complete/`)
  return response.data
}

// ---------------------------------------------------------------------------
// Pipeline Configs
// ---------------------------------------------------------------------------

export async function getConfigs(
  params?: Record<string, string>,
): Promise<PipelineConfigsResponse> {
  const response = await tripletsApi.get('/triplets/configs/', { params })
  return response.data
}

export async function createConfig(data: Partial<PipelineConfig>): Promise<PipelineConfig> {
  const response = await tripletsApi.post('/triplets/configs/', data)
  return response.data
}

export async function getConfig(configId: number): Promise<PipelineConfig> {
  const response = await tripletsApi.get(`/triplets/configs/${configId}/`)
  return response.data
}

export async function updateConfig(
  configId: number,
  data: Partial<PipelineConfig>,
): Promise<PipelineConfig> {
  const response = await tripletsApi.put(`/triplets/configs/${configId}/`, data)
  return response.data
}

export async function deleteConfig(configId: number): Promise<void> {
  await tripletsApi.delete(`/triplets/configs/${configId}/`)
}

// ---------------------------------------------------------------------------
// Tenants
// ---------------------------------------------------------------------------

export async function getTenants(
  params?: Record<string, string>,
): Promise<{ results: PipelineConfigSummary[] }> {
  const response = await tripletsApi.get('/triplets/tenants/', { params })
  return response.data
}

export async function getTenant(name: string): Promise<TenantDetail> {
  const response = await tripletsApi.get(`/triplets/tenants/${name}/`)
  return response.data
}

export async function getTenantSession(name: string): Promise<TenantSession> {
  const response = await tripletsApi.get(`/triplets/tenants/${name}/session/`)
  return response.data
}

export async function updateTenantSession(
  name: string,
  data: Partial<TenantSession>,
): Promise<TenantSession> {
  const response = await tripletsApi.put(`/triplets/tenants/${name}/session/`, data)
  return response.data
}

export async function getTenantTests(name: string): Promise<TestFileSummary[]> {
  const response = await tripletsApi.get(`/triplets/tenants/${name}/tests/`)
  return response.data
}

export async function getTenantTestResults(name: string): Promise<TestResultSummary[]> {
  const response = await tripletsApi.get(`/triplets/tenants/${name}/test-results/`)
  return response.data
}

export async function getTenantPendingReviews(name: string): Promise<PendingReview[]> {
  const response = await tripletsApi.get(`/triplets/tenants/${name}/pending-reviews/`)
  return response.data
}

export async function updatePendingReview(
  name: string,
  reviewId: number,
  data: { status: 'approved' | 'rejected' },
): Promise<PendingReview> {
  const response = await tripletsApi.put(
    `/triplets/tenants/${name}/pending-reviews/${reviewId}/`,
    data,
  )
  return response.data
}

export async function getTenantLogs(name: string, n = 200): Promise<LogsResponse> {
  const response = await tripletsApi.get(`/triplets/tenants/${name}/logs/`, { params: { n } })
  return response.data
}

export async function getTenantOutputs(name: string): Promise<OutputFile[]> {
  const response = await tripletsApi.get(`/triplets/tenants/${name}/outputs/`)
  return response.data
}

// ---------------------------------------------------------------------------
// Operations
// ---------------------------------------------------------------------------

export async function importTenants(
  name?: string,
): Promise<{ git: string; import: string[]; tenant_count: number }> {
  const response = await tripletsApi.post('/triplets/import-tenants/', null, {
    params: name ? { name } : undefined,
  })
  return response.data
}

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export async function getHealth(): Promise<HealthResponse> {
  const response = await tripletsApi.get('/triplets/health/')
  return response.data
}

export default tripletsApi
